import 'vite/modulepreload-polyfill';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Swiper from 'swiper';
import { createApp, onMounted, ref } from 'vue';

import { useMasterProductCard } from '~/composables/use-master-product-card';
import { ANIMATION_CONFIGS } from '~/constants/animations';
import * as DataDogLogs from '~/lib/datadog/datadog';

import * as AnalyticsData from '../analytics/analytics-data';
import { BaseSection } from './base-section';

/**
 * @type {import('swiper/types').SwiperOptions}
 */
const CAROUSEL_OPTIONS = {
  breakpoints: {
    0: {
      slidesPerView: 1.3,
    },
    1024: {
      slidesPerView: 3.6,
    },
    768: {
      slidesPerView: 2.6,
    },
  },
  loop: true,
  roundLengths: true,
  slidesOffsetBefore: 10,
  spaceBetween: 10,
  updateOnWindowResize: true,
};

const SECTION_NAME = 'standard-product-carousel';

class StandardProductCarousel extends BaseSection {
  constructor() {
    super(`[data-section-type="${SECTION_NAME}"]`);
  }

  init(instanceData) {
    const container = instanceData.container;
    const sectionInstance = this;

    createApp({
      name: 'StandardProductCarouselSection',
      setup() {
        const { cardRefs, setActiveColorway } = useMasterProductCard(container);
        const swiperInstance = ref(null);
        const activeProductGroup = ref(null);

        const handleCarouselPrevious = () => {
          swiperInstance.value.slidePrev();
        };

        const handleCarouselNext = () => {
          swiperInstance.value.slideNext();
        };

        /**
         * Animates the carousel content using GSAP
         * @param {HTMLElement} element - Element to animate
         * @param {boolean} fadeIn - Whether to fade in or out
         * @returns {Promise} - Promise that resolves when animation completes
         */
        const animateCarouselContent = (element) => {
          gsap.fromTo(
            element,
            { opacity: 0 },
            {
              ...ANIMATION_CONFIGS.FADE_IN,
              duration: ANIMATION_CONFIGS.FADE_IN.duration * 1.5,
            },
          );
        };

        const handleProductGroupSwitch = async (productGroupId) => {
          // If we have an active product group, move its slides back to its original container
          if (activeProductGroup.value && swiperInstance.value) {
            const currentContainer = container.querySelector(
              `[data-${SECTION_NAME}-block-id="${activeProductGroup.value}"]`,
            );
            if (currentContainer) {
              const currentContainerWrapper = currentContainer.querySelector('.swiper-wrapper');
              const mainList = container.querySelector('.swiper .swiper-wrapper');
              const currentSlides = mainList.querySelectorAll('.swiper-slide');

              // Move current slides back to their original container
              Array.from(currentSlides).forEach((slide) => {
                currentContainerWrapper.appendChild(slide);
              });
            }
          }

          activeProductGroup.value = productGroupId;

          // Find the carousel container for this category
          const productGroupContainer = container.querySelector(
            `[data-${SECTION_NAME}-block-id="${productGroupId}"]`,
          );
          if (!productGroupContainer) return;

          // Get all slides from the new category
          const newSlides = productGroupContainer.querySelectorAll('.swiper-slide');

          // Update the current swiper instance with new slides
          if (swiperInstance.value) {
            swiperInstance.value.destroy();
          }

          // Move slides to main carousel (preserve Vue bindings by moving original elements)
          const mainCarousel = container.querySelector('.swiper');
          const mainList = mainCarousel.querySelector('.swiper-wrapper');
          mainList.innerHTML = '';

          // Move original slides instead of cloning to preserve Vue reactive bindings
          Array.from(newSlides).forEach((slide) => {
            mainList.appendChild(slide);
          });

          // Reinitialize Swiper with new slides
          swiperInstance.value = new Swiper(mainCarousel, CAROUSEL_OPTIONS);

          // Fade in the new slides using GSAP
          animateCarouselContent(mainList);
        };

        onMounted(async () => {
          const initialProductGroupId = container.getAttribute('data-initial-product-group-id');

          try {
            if (initialProductGroupId) {
              await handleProductGroupSwitch(initialProductGroupId);
            }
          } catch (error) {
            DataDogLogs.error(
              '[Allbirds section] - Standard Product Carousel - Error initializing carousel:',
              {},
              error,
            );
          }

          // Hook analytics event to CTA links
          if (sectionInstance.isHomePageTemplate()) {
            AnalyticsData.hookHomepageModuleClicked({
              sectionName: SECTION_NAME,
            });
          }
        });

        return {
          activeProductGroup,
          cardRefs,
          handleCarouselNext,
          handleCarouselPrevious,
          handleProductGroupSwitch,
          setActiveColorway,
        };
      },
    }).mount(container);
  }

  /**
   * Sets up a ScrollTrigger animation instead of using IntersectionObserver
   * This is used to initialize the animation with ScrollTrigger instead of the default behavior
   *
   * @param {Object} instanceData - The section instance data
   */
  setupInViewAnimation(instanceData) {
    if (instanceData.animation) {
      instanceData.animation.paused(false);

      ScrollTrigger.refresh();
    } else {
      const animation = this.setupAnimation(instanceData, () =>
        this.setupInViewAnimationTimeline(instanceData),
      );

      if (animation && animation.scrollTrigger) {
        instanceData.scrollTrigger = animation.scrollTrigger;
      }
    }
  }

  /**
   * Sets up the animation timeline for the section that will play when it comes into view
   * This will be called automatically during initialization
   *
   * @param {Object} instanceData - The section instance data
   * @return {Object} The GSAP timeline for the animation
   */
  setupInViewAnimationTimeline(instanceData) {
    const container = instanceData.container;
    if (!container) return null;

    const tl = gsap.timeline({
      paused: true,
      scrollTrigger: {
        ...this.getInViewAnimationOptions(),
        start: 'top 100%',
        trigger: container,
      },
    });

    if (!tl) return null;
    tl.fromTo(container, { opacity: 0 }, ANIMATION_CONFIGS.FADE_IN, 0);

    return tl;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new StandardProductCarousel();
});
